import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth, API } from '../App';
import axios from 'axios';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, fetchUser } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already_verified
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  
  const tokenParam = searchParams.get('token');

  useEffect(() => {
    if (tokenParam) {
      verifyEmail(tokenParam);
    } else {
      // No token - show info page about verification
      setStatus('info');
    }
  }, [tokenParam]);

  const verifyEmail = async (verificationToken) => {
    try {
      const response = await axios.post(`${API}/auth/verify-email`, {
        token: verificationToken
      });
      
      if (response.data.already_verified) {
        setStatus('already_verified');
        setMessage(response.data.message);
      } else {
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email);
        
        // Refresh user data if logged in
        if (token) {
          await fetchUser();
        }
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.response?.data?.detail || 'Verification failed. The link may have expired.');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'verifying':
        return (
          <div className="text-center py-8">
            <Loader2 className="w-16 h-16 mx-auto text-emerald-600 animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">
              Verifying your email...
            </h2>
            <p className="text-muted-foreground mt-2">Please wait a moment</p>
          </div>
        );
      
      case 'success':
        return (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto text-emerald-600 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">
              Email Verified!
            </h2>
            <p className="text-muted-foreground mt-2 mb-6">
              {email ? `Your email ${email} has been verified.` : message}
            </p>
            <Button 
              onClick={() => navigate(user ? '/dashboard' : '/login')}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            >
              {user ? 'Go to Dashboard' : 'Login Now'}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        );
      
      case 'already_verified':
        return (
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 mx-auto text-blue-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">
              Already Verified
            </h2>
            <p className="text-muted-foreground mt-2 mb-6">
              Your email has already been verified. You're all set!
            </p>
            <Button 
              onClick={() => navigate(user ? '/dashboard' : '/login')}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            >
              {user ? 'Go to Dashboard' : 'Login Now'}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        );
      
      case 'error':
        return (
          <div className="text-center py-8">
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">
              Verification Failed
            </h2>
            <p className="text-muted-foreground mt-2 mb-6">{message}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                variant="outline"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
              <Link to="/signup">
                <Button className="bg-emerald-600 hover:bg-emerald-700">
                  Sign Up Again
                </Button>
              </Link>
            </div>
          </div>
        );
      
      case 'info':
      default:
        return (
          <div className="text-center py-8">
            <Mail className="w-16 h-16 mx-auto text-emerald-600 mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200">
              Email Verification
            </h2>
            <p className="text-muted-foreground mt-2 mb-6">
              Check your inbox for a verification link. If you haven't received it, you can request a new one after logging in.
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Go to Login
            </Button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">T</span>
            </div>
            <span className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">{process.env.REACT_APP_APP_NAME || 'Flowteras'}</span>
          </Link>
          <CardTitle>Email Verification</CardTitle>
          <CardDescription>Confirm your email address</CardDescription>
        </CardHeader>
        <CardContent>
          {renderContent()}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
